const BASE_URL = `${process.env.REACT_APP_LATTICE_SUPABASE_URL}/functions/v1/public`;

const headers = {
  "x-api-key": process.env.REACT_APP_LATTICE_PUBLIC_API_KEY || "",
  "x-workspace-id": process.env.REACT_APP_LATTICE_WORKSPACE_ID || "",
};

function buildQuery(
  params: Record<string, string | number | undefined>
): string {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== "") sp.set(k, String(v));
  });
  return sp.toString() ? `?${sp.toString()}` : "";
}

async function apiFetch<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, { headers });
  if (!res.ok) {
    throw new Error(`API error: ${res.status} ${res.statusText}`);
  }
  return res.json();
}

// ---------- Pagination ----------

export interface Pagination {
  page: number;
  limit: number;
  total_count: number;
  total_pages: number;
}

// ---------- Startups ----------

export interface ApiStage {
  id: string;
  name: string;
  slug: string;
  description?: string;
}

export interface ApiStartup {
  id: string;
  name: string;
  description?: string;
  introduction?: string;
  profile_image?: {
    object_url: string;
    name: string;
    size: number;
    type: string;
  }[];
  ceo_name?: string;
  establishment_date?: string;
  homepage_url?: string;
  address?: { country?: string; address_line?: string };
  industry_names?: string[];
  stage_id?: string;
  is_header: boolean;
  manager?: string;
  supporters?: string[];
  // 클라이언트에서 stages 목록으로 매핑하여 채우는 필드
  stage?: string;
}

interface StartupsJson {
  data: { startups: ApiStartup[]; stages: ApiStage[]; industries: string[] };
  pagination: Pagination;
}

export interface StartupsResponse {
  startups: ApiStartup[];
  stages: ApiStage[];
  industries: string[];
  pagination: Pagination;
}

export interface GetStartupsParams {
  page?: number;
  limit?: number;
  search?: string;
  industry?: string;
  stage?: string;
  is_header?: boolean;
  order?: "name" | "establishment_date" | "created_at" | "display_order";
  dir?: "asc" | "desc";
  random?: boolean;
  random_seed?: number;
}

export async function getStartups(
  params: GetStartupsParams = {}
): Promise<StartupsResponse> {
  const json = await apiFetch<StartupsJson>(
    `/startups${buildQuery({
      page: params.page,
      limit: params.limit,
      search: params.search,
      industry: params.industry,
      stage: params.stage,
      is_header: params.is_header ? "true" : undefined,
      order: params.order,
      dir: params.dir,
      random: params.random ? "true" : undefined,
      random_seed: params.random_seed?.toString(),
    })}`
  );
  return { ...json.data, pagination: json.pagination };
}

export async function getStartup(id: string): Promise<ApiStartup> {
  const json = await apiFetch<{ data: ApiStartup }>(`/startups/${id}`);
  return json.data;
}

// ---------- Members ----------

export interface ApiTeam {
  id: string;
  name: string;
  description?: string;
}

export interface ApiTeamWithMembers extends ApiTeam {
  members: ApiMember[];
}

export interface ApiMemberPortfolio {
  name: string;
  role: string;
}

export interface ApiMemberPortfolioDetail {
  id: string;
  name: string;
  role: string;
  description?: string;
  introduction?: string;
  profile_image?: {
    object_url: string;
    name: string;
    size: number;
    type: string;
  }[];
  industry_names?: string[];
  stage_id?: string;
  stage?: string;
}

export interface ApiMember {
  id: string;
  name: string;
  email?: string;
  introduction?: string;
  profile_image?: {
    object_url: string;
    name: string;
    size: number;
    type: string;
  }[];
  keywords?: string[];
  education?: { school: string; major?: string; degree?: string }[];
  career?: { company: string; position?: string; period?: string }[];
  sns_links?: Record<string, string>;
  position_name?: string;
  organization_names?: string[];
  leader_role?: string;
  industry_ids?: string[];
  team_id?: string;
  portfolios?: ApiMemberPortfolio[];
}

export interface ApiMemberDetail extends Omit<ApiMember, "portfolios"> {
  portfolios?: ApiMemberPortfolioDetail[];
}

export interface MembersResponse {
  teams: ApiTeamWithMembers[];
}

export async function getMembers(): Promise<MembersResponse> {
  const json = await apiFetch<{ data: MembersResponse }>("/members");
  return json.data;
}

export async function getMember(id: string): Promise<ApiMemberDetail> {
  const json = await apiFetch<{ data: ApiMemberDetail }>(`/members/${id}`);
  return json.data;
}

export interface MemberPortfoliosResponse {
  portfolios: ApiMemberPortfolioDetail[];
  pagination: Pagination;
}

export async function getMemberPortfolios(
  memberId: string,
  page = 1,
  limit = 6
): Promise<MemberPortfoliosResponse> {
  const json = await apiFetch<{
    data: ApiMemberPortfolioDetail[];
    pagination: Pagination;
  }>(`/members/${memberId}/portfolios${buildQuery({ page, limit })}`);
  return { portfolios: json.data, pagination: json.pagination };
}

// ---------- News ----------

export interface ApiNews {
  id: string;
  title?: string;
  content?: string;
  category: string;
  article_url: string;
  thumbnail_url?: string;
  published_date: string;
  startup_name?: string;
}

interface NewsJson {
  data: ApiNews[];
  pagination: Pagination;
}

export interface NewsResponse {
  news: ApiNews[];
  pagination: Pagination;
}

export interface GetNewsParams {
  page?: number;
  limit?: number;
  search?: string;
  category?: string;
}

export async function getNews(
  params: GetNewsParams = {}
): Promise<NewsResponse> {
  const json = await apiFetch<NewsJson>(
    `/news${buildQuery({
      page: params.page,
      limit: params.limit,
      search: params.search,
      category: params.category,
    })}`
  );
  return { news: json.data, pagination: json.pagination };
}

export async function getNewsById(id: string): Promise<ApiNews> {
  const json = await apiFetch<{ data: ApiNews }>(`/news/${id}`);
  return json.data;
}

export async function getNewsByStartupId(
  startupId: string,
  page = 1,
  limit = 20
): Promise<NewsResponse> {
  const json = await apiFetch<NewsJson>(
    `/news/portfolio/${startupId}${buildQuery({ page, limit })}`
  );
  return { news: json.data, pagination: json.pagination };
}
